import { HttpError } from 'react-admin';

import { execAdmin, execAdminForm } from '@src/apis/reware/utils';
import { requirePartner } from '@src/providers/dataProvider/shared';

import type {
  UploadAndStageMarkdownsMutation,
  UploadAndStageMarkdownsMutationVariables,
  StagingReviewQuery,
  StagingReviewQueryVariables,
  DiscardStagedAdjustmentMutation,
  DiscardStagedAdjustmentMutationVariables,
  ConfirmStagedAdjustmentMutation,
  ConfirmStagedAdjustmentMutationVariables,
  OverrideProgressQuery,
  OverrideProgressQueryVariables,
} from '@src/types/__generated/rewareAdminTypes';

// ---------------------------------------------------------------------------
// Derived types — unwrapped from generated query/mutation response types
// ---------------------------------------------------------------------------

export type StagingReviewResponse = NonNullable<NonNullable<StagingReviewQuery['partner']>['stagingReview']>;
export type StagingItem = StagingReviewResponse['items'][number];
export type StagingReviewSummaryData = StagingReviewResponse['summary'];
export type ValidationError = StagingItem['validationErrors'][number];
export type ValidationStatus = StagingItem['validationStatus'];
export type OverrideProgress = NonNullable<NonNullable<OverrideProgressQuery['partner']>['overrideProgress']>;

type DiscardStagingResponse = NonNullable<NonNullable<DiscardStagedAdjustmentMutation['partner']>['discardStagedAdjustment']>;
type ConfirmStagingResponse = NonNullable<NonNullable<ConfirmStagedAdjustmentMutation['partner']>['confirmStagedAdjustment']>;

const gql = String.raw;

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function uploadAndStage(file: File): Promise<{ adjustmentId: number }> {
  const partner = requirePartner();
  const query = gql`
    mutation uploadAndStageMarkdowns($partnerId: Int!) {
      partner(id: $partnerId) {
        uploadAndStageMarkdowns {
          id
          initiatedAt
        }
      }
    }
  `;

  const data = await execAdminForm<UploadAndStageMarkdownsMutationVariables, UploadAndStageMarkdownsMutation>(
    query,
    { partnerId: partner.id },
    { markdownCsv: file },
  );

  const result = data.partner?.uploadAndStageMarkdowns;
  if (!result) {
    throw new HttpError(
      'Upload completed but the server returned no confirmation. Please refresh.',
      502,
    );
  }

  return { adjustmentId: result.id };
}

export async function fetchStagingReview(adjustmentId: number): Promise<StagingReviewResponse> {
  const partner = requirePartner();
  const query = gql`
    query stagingReview($partnerId: Int!, $adjustmentId: Int!) {
      partner(id: $partnerId) {
        stagingReview(adjustmentId: $adjustmentId) {
          adjustmentId
          items {
            id
            pricingAdjustmentId
            rowNumber
            parentSku
            skuSubcode
            color
            sizeCode
            yadjustedCode
            price
            validationStatus
            validationErrors {
              code
              message
              relatedRowNumbers
            }
            affectedCatalogItemCount
            affectedInventoryCount
          }
          summary {
            totalRows
            validCount
            warningCount
            errorCount
          }
        }
      }
    }
  `;

  const data = await execAdmin<StagingReviewQueryVariables, StagingReviewQuery>(
    query,
    { variables: { partnerId: partner.id, adjustmentId } },
  );

  const result = data.partner?.stagingReview;
  if (!result) {
    throw new HttpError(`No staging data found for adjustment ${adjustmentId}`, 404);
  }

  return result;
}

export async function discardStaging(adjustmentId: number): Promise<DiscardStagingResponse> {
  const partner = requirePartner();
  const query = gql`
    mutation discardStagedAdjustment($partnerId: Int!, $adjustmentId: Int!) {
      partner(id: $partnerId) {
        discardStagedAdjustment(adjustmentId: $adjustmentId) {
          adjustmentId
          deletedCount
        }
      }
    }
  `;

  const data = await execAdmin<DiscardStagedAdjustmentMutationVariables, DiscardStagedAdjustmentMutation>(
    query,
    { variables: { partnerId: partner.id, adjustmentId } },
  );

  const result = data.partner?.discardStagedAdjustment;
  if (!result) {
    throw new HttpError('Discard failed — no response from server', 502);
  }

  return result;
}

export async function confirmStaging(adjustmentId: number): Promise<ConfirmStagingResponse> {
  const partner = requirePartner();
  const query = gql`
    mutation confirmStagedAdjustment($partnerId: Int!, $adjustmentId: Int!) {
      partner(id: $partnerId) {
        confirmStagedAdjustment(adjustmentId: $adjustmentId) {
          adjustmentId
          confirmedCount
        }
      }
    }
  `;

  const data = await execAdmin<ConfirmStagedAdjustmentMutationVariables, ConfirmStagedAdjustmentMutation>(
    query,
    { variables: { partnerId: partner.id, adjustmentId } },
  );

  const result = data.partner?.confirmStagedAdjustment;
  if (!result) {
    throw new HttpError('Confirm failed — no response from server', 502);
  }

  return result;
}

export async function fetchOverrideProgress(): Promise<OverrideProgress | null> {
  const partner = requirePartner();
  const query = gql`
    query overrideProgress($partnerId: Int!) {
      partner(id: $partnerId) {
        overrideProgress {
          adjustmentId
          initiatedAt
          location
          totalRows
          pricingJobSynced
          pricingJobTotal
          shopifySynced
          shopifyTotal
        }
      }
    }
  `;

  const data = await execAdmin<OverrideProgressQueryVariables, OverrideProgressQuery>(
    query,
    { variables: { partnerId: partner.id } },
  );

  return data.partner?.overrideProgress ?? null;
}
